import { TCompanies, TRoles } from './const'

/*
 * Wer darf über den Hilfe-Knopf eine Anfrage stellen?
 *
 * Dieselbe Regel steht im Backend in `services/tickets/requestChannel.js` —
 * dort wird sie entschieden, und dort steht auch die ausführliche Begründung.
 * Hier steht sie, damit die Einstellungen anzeigen können, was gerade gilt.
 *
 * Der Hilfe-Knopf selbst fragt sie **nicht** nach: Ob er etwas bewirkt, sagt
 * `profile.mayCreateRequest` aus dem eigenen Profil. Der Grund ist die
 * Lehrkraft — sie betreut mehrere Junior Companies, und deren
 * Unternehmensformen kennt das Frontend ohne weiteren Aufruf nicht.
 *
 * **Fehlt die Einstellung, ist alles offen.** Andersherum als beim Upload
 * durch die Lehrkraft (`shared/teacherDocumentUpload.js`, fehlend = aus): Dort
 * erteilte eine fehlende Zeile eine Freigabe, die niemand vergeben hat; hier
 * nähme sie einen Weg weg, den es bereits gibt.
 */

/** Die drei Rollen, die über den Hilfe-Knopf melden. */
export const REQUEST_CHANNEL_ROLES = [TRoles.STUDENT, TRoles.TEACHER, TRoles.SUPPORTER]

/** Nur diese beiden hängen an einer Junior Company. */
export const COMPANY_BOUND_ROLES = [TRoles.STUDENT, TRoles.TEACHER]

export const REQUEST_CHANNEL_COMPANY_TYPES = [TCompanies.JC, TCompanies.JCC, TCompanies.JBC]

const hatUnternehmensform = role => COMPANY_BOUND_ROLES.includes(role)

const readRoleConfig = (value, role) => {
	const eintrag = value && typeof value === 'object' ? value : {}
	const gebunden = hatUnternehmensform(role)

	return {
		active: eintrag.active !== false,
		companyTypes: gebunden
			? Array.isArray(eintrag.companyTypes)
				? eintrag.companyTypes.filter(type => REQUEST_CHANNEL_COMPANY_TYPES.includes(type))
				: [...REQUEST_CHANNEL_COMPANY_TYPES]
			: [],
		// Wer noch zu keiner Junior Company gehört, hat keine Unternehmensform,
		// an der sich die Freigabe messen ließe. Das steht als eigene Angabe da
		// und wird nicht erraten.
		withoutCompany: gebunden ? eintrag.withoutCompany !== false : true
	}
}

/** Der gespeicherte Stand, auf eine feste Form gebracht. */
export const readRequestChannelConfig = options =>
	REQUEST_CHANNEL_ROLES.reduce(
		(acc, role) => ({ ...acc, [role]: readRoleConfig(options?.requestChannel?.[role], role) }),
		{}
	)

/**
 * Darf diese Rolle mit diesen Unternehmensformen melden?
 *
 * Eine freigegebene Unternehmensform genügt: Wer eine JC und eine JBC betreut,
 * verlöre den Weg sonst wegen der JBC.
 */
export const mayRoleAsk = (config, role, companyTypes = []) => {
	if (!REQUEST_CHANNEL_ROLES.includes(role)) return true

	const eintrag = config?.[role]
	if (!eintrag) return true
	if (!eintrag.active) return false
	if (!hatUnternehmensform(role)) return true

	const formen = [...new Set((companyTypes || []).filter(Boolean))]
	if (!formen.length) return eintrag.withoutCompany

	return formen.some(type => eintrag.companyTypes.includes(type))
}
